import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent
} from "react";
import type { EChartsOption } from "echarts";
import type { LineSeriesOption } from "echarts/charts";
import type { EChartsType } from "echarts/core";
import type { MetricPoint } from "@roboscientist/contracts";
import { useThemePalette } from "../useThemePalette.js";
import { Chart } from "./Chart.js";
import { TENSORBOARD_SMOOTHING_MAX, tensorboardSmooth } from "./chartMath.js";

export type MetricComparisonCurve = {
  runId: string;
  label: string;
  fullLabel: string;
  color: string;
  points: MetricPoint[];
};

type MetricTooltipParam = {
  axisValue?: number | string;
  color?: string;
  data?: unknown;
  seriesId?: string;
  seriesName?: string;
  value?: unknown;
};
type MetricZoomWindow = { start: number; end: number };
type MetricGesture = {
  pointerId: number;
  mode: "zoom" | "pan";
  startX: number;
  currentX: number;
  window: MetricZoomWindow;
  moved: boolean;
};
type MetricZoomSelection = { x0: number; x1: number };

const METRIC_GRID_LEFT = 48;
const METRIC_GRID_RIGHT = 20;

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;"
})[character]!);

const metricValue = (param: MetricTooltipParam): number | null => {
  const source = Array.isArray(param.value) ? param.value : Array.isArray(param.data) ? param.data : null;
  const value = source?.[1];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const formatMetricValue = (value: number | null) => value == null
  ? "—"
  : value.toLocaleString(undefined, { maximumSignificantDigits: 7 });

const comparisonTooltip = (params: unknown, fullLabels: ReadonlyMap<string, string>, fallbackColor: string): string => {
  const list = (Array.isArray(params) ? params : [params]).filter(Boolean) as MetricTooltipParam[];
  if (!list.length) return "";
  const rows = new Map<string, { color: string; raw: number | null; smoothed: number | null; value: number | null }>();
  for (const param of list) {
    const name = param.seriesName ?? "Run";
    const row = rows.get(name) ?? { color: param.color ?? fallbackColor, raw: null, smoothed: null, value: null };
    const value = metricValue(param);
    if (param.seriesId?.startsWith("raw:")) row.raw = value;
    else if (param.seriesId?.startsWith("smooth:")) row.smoothed = value;
    else row.value = value;
    rows.set(name, row);
  }
  const step = escapeHtml(String(list[0]?.axisValue ?? "—"));
  const body = [...rows.entries()].map(([name, row]) => {
    const details = row.smoothed == null
      ? formatMetricValue(row.value ?? row.raw)
      : `<strong>${formatMetricValue(row.smoothed)}</strong><small>raw ${formatMetricValue(row.raw)}</small>`;
    return `<div class="metric-tooltip-row"><i style="background:${row.color}"></i><span>${escapeHtml(fullLabels.get(name) ?? name)}</span>${details}</div>`;
  }).join("");
  return `<div class="metric-tooltip"><header>step ${step}</header>${body}</div>`;
};

export function MetricComparisonChart({ metric, curves }: { metric: string; curves: MetricComparisonCurve[] }) {
  const { palette } = useThemePalette();
  const [smoothing, setSmoothing] = useState(0);
  const [zoomWindow, setZoomWindow] = useState<MetricZoomWindow>({ start: 0, end: 100 });
  const [zoomSelection, setZoomSelection] = useState<MetricZoomSelection | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const chartInstanceRef = useRef<EChartsType | null>(null);
  const interactionRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<MetricGesture | null>(null);
  const zoomWindowRef = useRef<MetricZoomWindow>({ start: 0, end: 100 });
  const chartText = palette.muted;

  const applyZoomWindow = (next: MetricZoomWindow) => {
    zoomWindowRef.current = next;
    setZoomWindow((current) =>
      Math.abs(current.start - next.start) < 0.0001 && Math.abs(current.end - next.end) < 0.0001
        ? current
        : next
    );
    chartInstanceRef.current?.dispatchAction({ type: "dataZoom", start: next.start, end: next.end });
  };

  useLayoutEffect(() => {
    const fullWindow = { start: 0, end: 100 };
    zoomWindowRef.current = fullWindow;
    setZoomWindow(fullWindow);
    setZoomSelection(null);
    chartInstanceRef.current?.dispatchAction({ type: "dataZoom", ...fullWindow });
  }, [metric]);

  useEffect(() => {
    const element = interactionRef.current;
    if (!element) return;
    const handleTrackpadPan = (event: WheelEvent) => {
      const horizontalDelta = event.deltaX;
      const horizontalGesture = Math.abs(horizontalDelta) > 0 && Math.abs(horizontalDelta) > Math.abs(event.deltaY);
      if (!event.ctrlKey && !horizontalGesture) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.ctrlKey || !horizontalGesture || gestureRef.current) return;

      const current = zoomWindowRef.current;
      const span = current.end - current.start;
      if (span >= 99.9999) return;

      const width = Math.max(1, element.getBoundingClientRect().width);
      const deltaInPixels = event.deltaMode === 1
        ? horizontalDelta * 16
        : event.deltaMode === 2
          ? horizontalDelta * width
          : horizontalDelta;
      const requestedShift = Math.max(-span * 0.25, Math.min(span * 0.25, deltaInPixels / width * span));
      const nextStart = Math.max(0, Math.min(100 - span, current.start + requestedShift));
      applyZoomWindow({ start: nextStart, end: nextStart + span });
    };
    element.addEventListener("wheel", handleTrackpadPan, { capture: true, passive: false });
    return () => element.removeEventListener("wheel", handleTrackpadPan, true);
  }, [curves.length, metric]);

  const smoothingEnabled = smoothing > 0;
  const fullLabels = new Map(curves.map((curve) => [curve.label, curve.fullLabel]));
  const chartSeries: LineSeriesOption[] = curves.flatMap((curve): LineSeriesOption[] => {
    const points = [...curve.points].sort((a, b) => a.step - b.step);
    const rawData = points.map((point) => [point.step, point.value]);
    if (!smoothingEnabled) {
      return [{
        id: `value:${curve.runId}`,
        name: curve.label,
        type: "line" as const,
        showSymbol: false,
        sampling: "lttb" as const,
        connectNulls: false,
        lineStyle: { color: curve.color, width: 1.8, opacity: 0.92 },
        itemStyle: { color: curve.color },
        emphasis: { disabled: true },
        data: rawData
      }];
    }

    const smoothed = tensorboardSmooth(points.map((point) => point.value), smoothing);
    return [{
      id: `smooth:${curve.runId}`,
      name: curve.label,
      type: "line" as const,
      z: 2,
      showSymbol: false,
      sampling: "lttb" as const,
      connectNulls: false,
      lineStyle: { color: curve.color, width: 2, opacity: 1 },
      itemStyle: { color: curve.color },
      emphasis: { disabled: true },
      data: points.map((point, index) => [point.step, smoothed[index] ?? point.value])
    }, {
      id: `raw:${curve.runId}`,
      name: curve.label,
      type: "line" as const,
      z: 1,
      showSymbol: false,
      sampling: "lttb" as const,
      connectNulls: false,
      lineStyle: { color: curve.color, width: 1, opacity: 0.3 },
      itemStyle: { color: curve.color, opacity: 0.3 },
      emphasis: { disabled: true },
      data: rawData
    }];
  });

  const option: EChartsOption = {
    animationDuration: 260,
    animationDurationUpdate: 180,
    color: curves.map((curve) => curve.color),
    grid: { left: METRIC_GRID_LEFT, right: METRIC_GRID_RIGHT, top: 52, bottom: 34 },
    legend: {
      type: "scroll",
      data: curves.map((curve) => ({
        name: curve.label,
        itemStyle: { color: curve.color, opacity: 1 },
        lineStyle: { color: curve.color, opacity: 1, width: 2 }
      })),
      left: 0,
      right: 0,
      top: 2,
      textStyle: { color: chartText, fontSize: 10 }
    },
    dataZoom: [{
      type: "inside",
      xAxisIndex: 0,
      filterMode: "filter",
      start: zoomWindow.start,
      end: zoomWindow.end,
      zoomOnMouseWheel: false,
      moveOnMouseMove: false,
      moveOnMouseWheel: false,
      preventDefaultMouseMove: false
    }],
    tooltip: {
      trigger: "axis",
      confine: true,
      transitionDuration: 0,
      axisPointer: { type: "line", snap: false },
      backgroundColor: palette.canvasOverlay,
      borderColor: palette.border,
      textStyle: { color: palette.foreground },
      formatter: (params: unknown) => comparisonTooltip(params, fullLabels, palette.muted)
    },
    xAxis: { type: "value", name: "step", nameTextStyle: { color: chartText }, axisLabel: { color: chartText }, splitLine: { show: false } },
    yAxis: { type: "value", scale: true, axisLabel: { color: chartText }, splitLine: { lineStyle: { color: palette.borderMuted } } },
    series: chartSeries
  };

  const prepareChartInteractions = (instance: EChartsType) => {
    chartInstanceRef.current = instance;
    const current = zoomWindowRef.current;
    instance.dispatchAction({ type: "dataZoom", start: current.start, end: current.end });
  };
  const metricX = (clientX: number, element: HTMLDivElement) => {
    const bounds = element.getBoundingClientRect();
    return Math.max(METRIC_GRID_LEFT, Math.min(bounds.width - METRIC_GRID_RIGHT, clientX - bounds.left));
  };
  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.button !== 2) return;
    const startX = metricX(event.clientX, event.currentTarget);
    const mode = event.button === 2 ? "pan" : "zoom";
    gestureRef.current = {
      pointerId: event.pointerId,
      mode,
      startX,
      currentX: startX,
      window: zoomWindowRef.current,
      moved: false
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setZoomSelection(null);
    if (mode === "pan") {
      event.preventDefault();
      setIsPanning(true);
    }
  };
  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const currentX = metricX(event.clientX, event.currentTarget);
    if (Math.abs(currentX - gesture.startX) < 3 && !gesture.moved) return;
    gesture.moved = true;
    gesture.currentX = currentX;
    event.preventDefault();
    if (gesture.mode === "pan") {
      const bounds = event.currentTarget.getBoundingClientRect();
      const plotWidth = Math.max(1, bounds.width - METRIC_GRID_LEFT - METRIC_GRID_RIGHT);
      const span = gesture.window.end - gesture.window.start;
      const requestedStart = gesture.window.start - (currentX - gesture.startX) / plotWidth * span;
      const nextStart = Math.max(0, Math.min(100 - span, requestedStart));
      applyZoomWindow({ start: nextStart, end: nextStart + span });
      setZoomSelection(null);
      return;
    }
    setZoomSelection({ x0: Math.min(gesture.startX, currentX), x1: Math.max(gesture.startX, currentX) });
  };
  const finishGesture = (event: ReactPointerEvent<HTMLDivElement>, applySelection = true) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const x0 = Math.min(gesture.startX, gesture.currentX);
    const x1 = Math.max(gesture.startX, gesture.currentX);
    if (gesture.mode === "zoom" && applySelection && gesture.moved && x1 - x0 >= 8) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const plotWidth = Math.max(1, bounds.width - METRIC_GRID_LEFT - METRIC_GRID_RIGHT);
      const current = gesture.window;
      const span = current.end - current.start;
      const nextStart = current.start + (x0 - METRIC_GRID_LEFT) / plotWidth * span;
      const nextEnd = current.start + (x1 - METRIC_GRID_LEFT) / plotWidth * span;
      applyZoomWindow({ start: nextStart, end: nextEnd });
    }
    gestureRef.current = null;
    setIsPanning(false);
    setZoomSelection(null);
  };
  const resetZoom = () => {
    applyZoomWindow({ start: 0, end: 100 });
    setZoomSelection(null);
  };

  return <div className="metric-comparison-chart">
    <div className="training-chart-controls">
      <label title="TensorBoard-style exponentially weighted smoothing. Set to zero to show only raw values.">
        <span>Smoothing</span>
        <input
          aria-label="Comparison smoothing"
          type="range"
          min="0"
          max={TENSORBOARD_SMOOTHING_MAX}
          step="0.001"
          value={smoothing}
          onChange={(event) => setSmoothing(Number(event.target.value))}
        />
        <output>{smoothing.toFixed(3)}</output>
      </label>
      <small>Drag to zoom · swipe/right-drag to pan · double-click to reset</small>
    </div>
    <div
      ref={interactionRef}
      className={`metric-chart-interaction metric-comparison-chart__interaction${isPanning ? " is-panning" : ""}`}
      data-zoom-start={zoomWindow.start}
      data-zoom-end={zoomWindow.end}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishGesture}
      onPointerCancel={(event) => finishGesture(event, false)}
      onContextMenu={(event) => event.preventDefault()}
      onDoubleClick={(event) => { event.preventDefault(); resetZoom(); }}
    >
      <Chart key={`comparison:${metric}`} option={option} style={{ height: 390 }} replaceMerge={["series"]} onChartReady={prepareChartInteractions} />
      {zoomSelection ? <div className="metric-zoom-selection" aria-hidden="true" style={{ "--selection-x": `${zoomSelection.x0}px`, "--selection-width": `${zoomSelection.x1 - zoomSelection.x0}px` } as CSSProperties} /> : null}
    </div>
  </div>;
}
